import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { useEffect, useId, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { discoverClient, type DiscoverApp } from "@/components/personal-apps/discoverClient";
import { discoveryNavigationTarget } from "@/components/personal-apps/discovery/discoveryUrl";
import { AppIcon } from "@/components/personal-apps/AppIcon";
import { useOptionalSearch } from "@/components/search/SearchProvider";

const SUGGESTION_LIMIT = 6;
const DEBOUNCE_MS = 200;

const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/**
 * App search in the site header. Typing only fills the suggestion popover;
 * the Discover grid, the side panel and a running app change when the search
 * is confirmed: Enter shows the results in Grid, a suggestion opens that app
 * in Preview. ⌘K / Ctrl+K focuses it from anywhere.
 */
export default function HeaderAppSearch({ onSearch, onOpenApp }: {
  /** Enter: show the matching apps in Discover's Grid. */
  onSearch: (query: string) => void;
  /** A suggestion: open that app in Preview (`target` is its selection key). */
  onOpenApp: (route: string, target: string) => void;
}) {
  useLocale();
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  // The confirmed search lives in the URL (`q`); the field is a draft of it.
  // A reload, a shared link or Back/Forward re-fills the field from the URL.
  const committed = useOptionalSearch()?.query.trim() ?? "";
  const [draft, setDraft] = useState(committed);
  useEffect(() => { setDraft(committed); }, [committed]);
  const [open, setOpen] = useState(false);
  const [apps, setApps] = useState<DiscoverApp[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  // Phones show an icon; tapping it lays the field over the header row.
  const [expanded, setExpanded] = useState(false);
  const collapse = () => { setOpen(false); setExpanded(false); };

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) collapse();
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const query = draft.trim();
    const timer = window.setTimeout(() => {
      setFailed(false);
      discoverClient.page(query ? { q: query, limit: SUGGESTION_LIMIT } : { sort: "popular", limit: SUGGESTION_LIMIT }, controller.signal)
        .then((page) => { setApps(page.apps.slice(0, SUGGESTION_LIMIT)); setActive(-1); },
          (cause) => { if (!controller.signal.aborted) { setApps([]); setFailed(true); void cause; } });
    }, query ? DEBOUNCE_MS : 0);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [draft, open]);

  const openApp = (app: DiscoverApp) => {
    collapse();
    setDraft("");
    input.current?.blur();
    onOpenApp(discoveryNavigationTarget({ selection: { kind: "discover", id: app.id }, query: "", themeId: null }), `discover:${app.id}`);
  };
  const confirm = () => {
    const query = draft.trim();
    collapse();
    input.current?.blur();
    onSearch(query);
  };
  const results = apps ?? [];
  return (
    <div className={`header-app-search${expanded ? " header-app-search--expanded" : ""}`} ref={wrap}>
      <button type="button" className="header-app-search-toggle" aria-label={t("Search")} aria-expanded={expanded}
        onClick={() => { setExpanded(true); setOpen(true); requestAnimationFrame(() => input.current?.focus()); }}>
        <Search size={20} aria-hidden="true" />
      </button>
      <div className="header-app-search-field ato-search">
        <Search size={18} aria-hidden="true" />
        <input
          ref={input}
          id="header-app-search"
          type="search"
          role="combobox"
          aria-label={t("Search")}
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          aria-autocomplete="list"
          placeholder={t("Search anything…")}
          maxLength={200}
          value={draft}
          onFocus={() => setOpen(true)}
          onChange={(event) => { setDraft(event.target.value); setOpen(true); }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActive((value) => Math.min(results.length - 1, value + 1)); }
            else if (event.key === "ArrowUp") { event.preventDefault(); setActive((value) => Math.max(-1, value - 1)); }
            else if (event.key === "Escape") { collapse(); input.current?.blur(); }
            else if (event.key === "Enter") {
              event.preventDefault();
              if (active >= 0 && results[active]) openApp(results[active]);
              else confirm();
            }
          }}
        />
        <kbd className="header-app-search-shortcut" aria-hidden="true">{isMac() ? "⌘ K" : "Ctrl K"}</kbd>
        <button type="button" className="header-app-search-close" aria-label={t("Close search")} onClick={collapse}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      {open ? (
        <div className="header-app-search-popover">
          <p className="header-app-search-heading">{draft.trim() ? <Message message="Apps" /> : <Message message="Popular" />}</p>
          <ul id={listId} role="listbox" aria-label={t("Search apps")}>
            {results.map((app, index) => (
              <li key={app.id} id={`${listId}-${index}`} role="option" aria-selected={index === active}
                className={index === active ? "header-app-search-option--active" : undefined}
                onPointerDown={(event) => event.preventDefault()}
                onPointerEnter={() => setActive(index)}
                onClick={() => openApp(app)}>
                <AppIcon name={app.title} seed={app.id} size={32} listingIconUrl={app.icon} />
                <span>
                  <strong>{app.title}</strong>
                  <small>{app.description.split(/\n\s*\n/, 1)[0]}</small>
                </span>
              </li>
            ))}
          </ul>
          {apps === null ? <p className="header-app-search-empty" role="status"><Message message="Loading apps…" /></p>
            : failed ? <p className="header-app-search-empty" role="alert"><Message message="Couldn't load apps." /></p>
            : !results.length ? <p className="header-app-search-empty" role="status"><Message message="No matching apps." /></p> : null}
          {draft.trim() ? (
            <button type="button" className="header-app-search-all" onPointerDown={(event) => event.preventDefault()} onClick={confirm}>
              <Search size={15} aria-hidden="true" /><Message message={"See all results for “{{value0}}”"} values={{ value0: draft.trim() }} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
